import type { UseFormGetValues, UseFormSetValue } from "react-hook-form";

import {
  RideFormFields,
  type IExtendableDto,
  type IRideDto,
  type IRideMetadata,
} from "@ryde/shared";

import { applyDirtyEditableFormMetadata } from "./ride-form-metadata.utils";
import type {
  TRideAdditionsAfterPriceRecalculate,
  TRideFormPriceMetadataWrite,
  TRidePriceRecalculateMetadataLayer,
} from "./ride-recalculate-price-metadata.models";

/**
 * What Recalculate must write onto the open ride form so Additions matches
 * the price breakdown (the dry run already applied the rate's additions).
 */
export const pickRideAdditionsAfterPriceRecalculate = ({
  additionIds,
  metadata,
}: {
  additionIds?: number[] | null;
  metadata?: IRideMetadata | null;
}): TRideAdditionsAfterPriceRecalculate => {
  const picked: TRideAdditionsAfterPriceRecalculate = {
    additionIds: Array.isArray(additionIds) ? additionIds : [],
    priceRateAdditions: metadata?.priceRateAdditions ?? null,
  };

  return picked;
};

/**
 * Same additions regardless of order; an unset value and an empty list are the same (nothing attached).
 * Recalculate uses it to leave the form clean when the dry run changed nothing.
 */
export const areRideAdditionIdsEqual = (
  current?: readonly number[] | null,
  next?: readonly number[] | null,
): boolean => {
  const currentIds = new Set(current ?? []);
  const nextIds = new Set(next ?? []);

  return (
    currentIds.size === nextIds.size &&
    [...nextIds].every(id => currentIds.has(id))
  );
};

/**
 * Recalc must not replace DTO metadata with the form snapshot.
 * Layer 1: rideDtoGlobal.metadata (stations, route, keys the form does not own).
 * Layer 2: dirty editable form keys — same rule as mergeFormValuesToMetadata.
 * Layer 3: price fields from the recalculate response, including priceRateAdditions.
 */
export const composeMetadataAfterPriceRecalculate = ({
  rideDtoGlobalMetadata,
  formValuesSnapshot,
  dirtyMetadataFields,
  priceLayer,
}: {
  rideDtoGlobalMetadata?: IRideMetadata;
  formValuesSnapshot?: Partial<IRideDto>;
  dirtyMetadataFields?: object;
  priceLayer: TRidePriceRecalculateMetadataLayer;
}): IRideMetadata => {
  const metadataAfterRecalc: IRideMetadata = {
    ...(rideDtoGlobalMetadata ?? {}),
  };

  applyDirtyEditableFormMetadata({
    formValuesSnapshot,
    dirtyMetadataFields,
    metadataComputed: metadataAfterRecalc,
  });

  const metadataWithPriceLayer: IRideMetadata = {
    ...metadataAfterRecalc,
    priceDetails: priceLayer.priceDetails,
    supplierPriceDetails: priceLayer.supplierPriceDetails,
    supplierManualPrice: priceLayer.supplierManualPrice,
    priceRateAdditions: priceLayer.priceRateAdditions,
  };

  return metadataWithPriceLayer;
};

/**
 * Nested form paths only — never `setValue("metadata", wholeObject)`.
 * `priceRateAdditions` is watched for chip highlight; it is not marked dirty.
 */
export const rideFormPriceMetadataWritesAfterRecalculate = (
  priceLayer: TRidePriceRecalculateMetadataLayer,
): readonly TRideFormPriceMetadataWrite[] => {
  const writes: TRideFormPriceMetadataWrite[] = [
    {
      name: RideFormFields.priceDetails,
      value: priceLayer.priceDetails,
      shouldDirty: false,
    },
    {
      name: RideFormFields.supplierPriceDetails,
      value: priceLayer.supplierPriceDetails,
      shouldDirty: false,
    },
    {
      name: RideFormFields.supplierManualPrice,
      value: priceLayer.supplierManualPrice,
      shouldDirty: true,
    },
    {
      name: RideFormFields.priceRateAdditions,
      value: priceLayer.priceRateAdditions,
      shouldDirty: false,
    },
  ];

  return writes;
};

/**
 * An unset value and `null` are the same (no supplier manual price). A write that only swaps one for the other
 * must be skipped: the form compares with its default by deep equality and would turn dirty.
 */
export const isRideSupplierManualPriceUnchanged = ({
  current,
  next,
}: {
  current: unknown;
  next: unknown;
}): boolean => (current ?? null) === (next ?? null);

/**
 * `hookFormContextAF` is `UseFormSetValue<IExtendableDto>`. Nested `metadata.*`
 * paths resolve to `undefined` because `IExtendableDto.metadata` is `object`.
 * `never` is required so PathValue accepts the real price-field values.
 */
export const applyRideFormPriceMetadataWritesAfterRecalculate = ({
  setValue,
  getValues,
  priceLayer,
}: {
  setValue?: UseFormSetValue<IExtendableDto>;
  getValues?: UseFormGetValues<IExtendableDto>;
  priceLayer: TRidePriceRecalculateMetadataLayer;
}): void => {
  if (!setValue) {
    return;
  }

  for (const formWrite of rideFormPriceMetadataWritesAfterRecalculate(
    priceLayer,
  )) {
    if (
      formWrite.name === RideFormFields.supplierManualPrice &&
      isRideSupplierManualPriceUnchanged({
        current: getValues?.(formWrite.name),
        next: formWrite.value,
      })
    ) {
      continue;
    }

    setValue(formWrite.name, formWrite.value as never, {
      shouldDirty: formWrite.shouldDirty,
    });
  }
};
